import type {
    ContentDefinition,
    ContentOutput,
    ForBlock as ForBlockType,
    ForDefinition,
    ForOutput,
    ForOutputMetadata,
    ImportCachedOutput,
    ImportBinding,
    ImportDocumentSnapshot,
    PluginDefinition,
    PluginOutput,
    RenderMode,
} from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import { ChevronDown, ChevronUp, Loader2, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AnnotationProvider } from "../../annotations/AnnotationProvider";
import { getImportCachedSymbolOutput } from "../../../lib/import-cache-output";
import type {
    ContentExecutionEntry,
    ForExecutionEntry,
    ForIterationEntry,
    ImportExecutionEntry,
} from "../../../store/plugin-execution-store";
import { getDocumentBundleDir } from "../../../lib/publish-utils";
import { getPlatformService } from "../../../platform";
import {
    DocContextProvider,
    DocOutputsProvider,
    useDocContext,
    type DocContextValue,
} from "../contexts/DocContext";
import {
    ExecutionContextProvider,
    useExecutionSessionConnected,
    useImportExecution,
    type ExecutionContextValue,
} from "../contexts/ExecutionContext";
import { BlockList } from "./BlockList";
import { CollapsedUnitRenderHeader } from "./CollapsedUnitRenderHeader";
import { readRenderExpanded } from "./render-attrs";

function recordToMap<T>(record: Record<string, T> | undefined): Map<string, T> {
    return new Map(Object.entries(record ?? {}) as [string, T][]);
}

function stripDefinitionOutput(definition: PluginDefinition): PluginDefinition {
    if (definition.output === undefined) return definition;
    const { output: _output, ...rest } = definition;
    return rest;
}

function getSnapshotLabel(snapshot: ImportDocumentSnapshot, namespace: string): string {
    const source = snapshot.resolvedPath ?? snapshot.source ?? namespace;
    const parts = source.split(/[\\/]/).filter(Boolean);
    return parts.at(-1) ?? namespace;
}

function buildForBlocksMap(blocks: ImportDocumentSnapshot["blocks"]): Map<string, ForBlockType> | undefined {
    const map = new Map<string, ForBlockType>();
    for (const block of blocks) {
        if (block.type === "for-block") map.set(block.id, block);
    }
    return map.size > 0 ? map : undefined;
}

function forOutputToExecution(forId: string, output: ForOutput): ForExecutionEntry {
    const meta = output.__metadata as ForOutputMetadata | undefined;
    return {
        forId,
        status: (meta?.failed ?? 0) > 0 && (meta?.succeeded ?? 0) === 0 ? "error" : "success",
        totalIterations: meta?.total ?? output.iterations.length,
        completedIterations: output.iterations.length,
        iterations: output.iterations.map((iter, idx): ForIterationEntry => ({
            index: iter.index ?? idx,
            item: iter.item,
            outputs: iter.outputs ?? {},
            ...(iter.definitions ? { definitions: iter.definitions } : {}),
            ...(iter.contentDefinitions ? { contentDefinitions: iter.contentDefinitions } : {}),
            success: iter.success ?? true,
            ...(iter.error ? { error: iter.error } : {}),
        })),
        startedAt: null,
        duration: meta?.duration ?? null,
        meta: meta
            ? {
                total: meta.total,
                succeeded: meta.succeeded,
                failed: meta.failed,
                duration: meta.duration,
                startedAt: meta.startedAt,
            }
            : undefined,
    };
}

function mergeImportOutputIntoSnapshotMaps(
    namespace: string,
    output: ImportCachedOutput,
    definitions: Map<string, PluginDefinition>,
    contentDefinitions: Map<string, ContentDefinition>,
    forDefinitions: Map<string, ForDefinition>,
    outputs: Map<string, PluginOutput>,
    contentOutputs: Map<string, ContentOutput>,
    forOutputs: Map<string, ForOutput>,
): void {
    const symbols = [...(output.symbols ?? []), ...(output.instanceSymbols ?? [])];
    for (const sym of symbols) {
        if (sym.kind === "node" && sym.pluginName && !definitions.has(sym.qualifiedId)) {
            const cachedOutput = getImportCachedSymbolOutput(namespace, output, sym);
            definitions.set(sym.qualifiedId, {
                id: sym.qualifiedId,
                pluginName: sym.pluginName,
                config: sym.config ?? {},
                template: sym.template ?? false,
                params: sym.params,
            });
            if (cachedOutput) outputs.set(sym.qualifiedId, cachedOutput as PluginOutput);
            continue;
        }

        if (sym.kind === "content" && sym.body != null && !contentDefinitions.has(sym.qualifiedId)) {
            const cachedOutput = getImportCachedSymbolOutput(namespace, output, sym);
            const contentOutput = cachedOutput as ContentOutput | undefined;
            contentDefinitions.set(sym.qualifiedId, {
                id: sym.qualifiedId,
                kind: "content",
                template: sym.template ?? false,
                body: sym.body,
                range: { start: 0, end: 0 },
                ...(sym.params ? { params: sym.params } : {}),
                ...(contentOutput ? { output: contentOutput } : {}),
            });
            if (contentOutput) contentOutputs.set(sym.qualifiedId, contentOutput);
            continue;
        }

        if (sym.kind === "for" && sym.forDefinition && !forDefinitions.has(sym.qualifiedId)) {
            const cachedOutput = getImportCachedSymbolOutput(namespace, output, sym);
            const forOutput = cachedOutput as ForOutput | undefined;
            forDefinitions.set(sym.qualifiedId, {
                ...sym.forDefinition,
                id: sym.qualifiedId,
                ...(forOutput ? { output: forOutput } : {}),
            });
            if (forOutput) forOutputs.set(sym.qualifiedId, forOutput);
        }
    }
}

function buildSnapshotRuntime(snapshot: ImportDocumentSnapshot): {
    definitions: Map<string, PluginDefinition>;
    contentDefinitions: Map<string, ContentDefinition>;
    forDefinitions: Map<string, ForDefinition>;
    outputs: Map<string, PluginOutput>;
    forExecutions: Map<string, ForExecutionEntry>;
    contentExecutions: Map<string, ContentExecutionEntry>;
    importExecutions: Map<string, ImportExecutionEntry>;
    importDocuments: Map<string, ImportDocumentSnapshot>;
} {
    const definitions = new Map<string, PluginDefinition>(
        [...recordToMap(snapshot.definitions)].map(([id, definition]) => [
            id,
            stripDefinitionOutput(definition),
        ] as [string, PluginDefinition]),
    );
    const contentDefinitions = recordToMap(snapshot.contentDefinitions);
    const forDefinitions = recordToMap(snapshot.forDefinitions);
    const outputs = recordToMap(snapshot.outputs);
    const contentOutputs = recordToMap(snapshot.contentOutputs);
    const forOutputs = recordToMap(snapshot.forOutputs);

    for (const [id, output] of contentOutputs) {
        const def = contentDefinitions.get(id);
        if (def && !def.output) contentDefinitions.set(id, { ...def, output });
    }

    const importExecutions = new Map<string, ImportExecutionEntry>();
    const importDocuments = new Map<string, ImportDocumentSnapshot>();
    for (const [namespace, output] of Object.entries(snapshot.imports ?? {})) {
        mergeImportOutputIntoSnapshotMaps(
            namespace,
            output,
            definitions,
            contentDefinitions,
            forDefinitions,
            outputs,
            contentOutputs,
            forOutputs,
        );
        importExecutions.set(namespace, {
            importId: namespace,
            status: output.__error ? "error" : "success",
            startedAt: null,
            duration: output.__metadata?.duration ?? null,
            symbols: output.symbols.map((sym) => ({
                qualifiedId: sym.qualifiedId,
                kind: sym.kind,
                publicName: sym.publicName,
            })),
            ...(output.document ? { document: output.document } : {}),
            ...(output.__error ? { error: output.__error.message } : {}),
            meta: {
                total: output.symbols.length,
                duration: output.__metadata?.duration ?? 0,
                source: output.source,
            },
        });
        if (output.document) importDocuments.set(namespace, output.document);
    }

    const forExecutions = new Map<string, ForExecutionEntry>();
    for (const [id, output] of forOutputs) {
        forExecutions.set(id, forOutputToExecution(id, output));
    }

    const contentExecutions = new Map<string, ContentExecutionEntry>();
    for (const [id, output] of contentOutputs) {
        contentExecutions.set(id, {
            contentId: id,
            status: "success",
            startedAt: null,
            duration: output.__metadata?.duration ?? null,
            output,
        });
    }

    return {
        definitions,
        contentDefinitions,
        forDefinitions,
        outputs,
        forExecutions,
        contentExecutions,
        importExecutions,
        importDocuments,
    };
}

export function ImportDocumentBlock({
    namespace,
    snapshot,
    mode,
    attrs,
    defaultExpanded = true,
}: {
    namespace: string;
    snapshot: ImportDocumentSnapshot;
    mode: RenderMode;
    attrs?: Record<string, string>;
    defaultExpanded?: boolean;
}) {
    const t = useT("editor");
    const parentDoc = useDocContext();
    const parentSessionConnected = useExecutionSessionConnected();
    const initialExpanded = readRenderExpanded(attrs, defaultExpanded);
    const [collapsed, setCollapsed] = useState(!initialExpanded);
    const runtime = useMemo(() => buildSnapshotRuntime(snapshot), [snapshot]);
    const forBlocksMap = useMemo(() => buildForBlocksMap(snapshot.blocks), [snapshot.blocks]);
    const importBindings = useMemo<ImportBinding[]>(
        () =>
            Object.entries(snapshot.imports ?? {}).map(([childNamespace, output]) => ({
                namespace: childNamespace,
                source: output.source,
                document: !!output.document,
                line: 0,
                output,
            })),
        [snapshot.imports],
    );
    const ownerNamespace = parentDoc.importRenderOwnerNamespace ?? namespace;
    const refreshNamespace = ownerNamespace;
    const importStatus = useImportExecution(refreshNamespace)?.status;
    const isImportRunning = importStatus === "running";
    const canRefresh = !!parentDoc.onRunImport && !!refreshNamespace;
    const documentPath = snapshot.resolvedPath ?? parentDoc.documentPath ?? null;
    const documentBundlePath = documentPath ? getDocumentBundleDir(documentPath) : parentDoc.documentBundlePath ?? null;
    const embeddedSourceContent = (snapshot as { sourceContent?: unknown }).sourceContent;
    const [sourceContent, setSourceContent] = useState(
        typeof embeddedSourceContent === "string" ? embeddedSourceContent : "",
    );

    useEffect(() => {
        setCollapsed(!initialExpanded);
    }, [namespace, initialExpanded]);

    useEffect(() => {
        let cancelled = false;
        if (typeof embeddedSourceContent === "string") {
            setSourceContent(embeddedSourceContent);
            return;
        }
        if (!snapshot.resolvedPath) {
            setSourceContent("");
            return;
        }
        try {
            getPlatformService().engine.workspaceReadFile({ path: snapshot.resolvedPath })
                .then(({ content }) => {
                    if (!cancelled) setSourceContent(content);
                })
                .catch(() => {
                    if (!cancelled) setSourceContent("");
                });
        } catch {
            setSourceContent("");
        }
        return () => {
            cancelled = true;
        };
    }, [embeddedSourceContent, snapshot.resolvedPath]);

    if (mode === "inline") {
        return (
            <span className="text-xs font-mono px-1 py-0.5 rounded bg-muted text-muted-foreground">
                {namespace}: import document render is block-only
            </span>
        );
    }

    const docValue: DocContextValue = {
        definitions: runtime.definitions,
        registry: parentDoc.registry,
        executable: false,
        documentPath,
        documentBundlePath,
        contentDefinitions: runtime.contentDefinitions,
        ...(forBlocksMap ? { forBlocksMap } : {}),
        ...(importBindings.length > 0 ? { importBindings } : {}),
        ...(runtime.importDocuments.size > 0 ? { importDocuments: runtime.importDocuments } : {}),
        importRenderOwnerNamespace: ownerNamespace,
        ...(parentDoc.callSidecar ? { callSidecar: parentDoc.callSidecar } : {}),
        ...(parentDoc.onApi ? { onApi: parentDoc.onApi } : {}),
        ...(parentDoc.onEngineNotification ? { onEngineNotification: parentDoc.onEngineNotification } : {}),
        ...(parentDoc.selectFile ? { selectFile: parentDoc.selectFile } : {}),
        ...(parentDoc.selectFolder ? { selectFolder: parentDoc.selectFolder } : {}),
        ...(parentDoc.openFile ? { openFile: parentDoc.openFile } : {}),
        ...(parentDoc.openExternal ? { openExternal: parentDoc.openExternal } : {}),
        ...(parentDoc.getAssetUrl ? { getAssetUrl: parentDoc.getAssetUrl } : {}),
        getDefinitions: () =>
            [...runtime.definitions.entries()].map(([id, def]) => ({
                id,
                pluginName: def.pluginName,
                hasOutput: !!runtime.outputs.get(id),
            })),
        ...(parentDoc.getTabs ? { getTabs: parentDoc.getTabs } : {}),
        ...(parentDoc.onRunImport ? { onRunImport: parentDoc.onRunImport } : {}),
    };

    const execValue: ExecutionContextValue = {
        forExecutions: runtime.forExecutions,
        contentExecutions: runtime.contentExecutions,
        importExecutions: runtime.importExecutions,
        sessionConnected: parentSessionConnected,
    };
    const label = getSnapshotLabel(snapshot, namespace);
    const delimiterClass = "flex items-center gap-2 text-[11px] font-mono text-muted-foreground";
    const markerClass = "inline-flex min-w-0 max-w-[70%] items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 hover:bg-muted hover:text-foreground";
    const lineClass = "h-px min-w-4 flex-1 bg-border";
    const toggleLabel = collapsed ? `Expand import ${label}` : `Collapse import ${label}`;

    if (collapsed) {
        return (
            <section className="my-4" data-import-document={namespace}>
                <CollapsedUnitRenderHeader
                    unitId={namespace}
                    label={t("render.importDocument.label")}
                    technicalLabel={`import@${namespace}${snapshot.source ? ` · ${snapshot.source}` : ""}`}
                    status={isImportRunning ? "running" : "success"}
                    statusLabels={{
                        running: t("render.importDocument.refreshing"),
                        success: t("render.importDocument.imported"),
                    }}
                    onExpand={() => setCollapsed(false)}
                />
            </section>
        );
    }

    return (
        <section className="my-4" data-import-document={namespace}>
            <div className={delimiterClass}>
                <span className={lineClass} aria-hidden="true" />
                <button
                    type="button"
                    className={markerClass}
                    onClick={() => setCollapsed((value) => !value)}
                    aria-expanded={!collapsed}
                    aria-label={toggleLabel}
                    title={`${toggleLabel}: ${snapshot.source}`}
                >
                    <ChevronDown size={12} className="shrink-0" />
                    <span className="truncate">{label}</span>
                </button>
                {isImportRunning && (
                    <span className="text-[10px] text-muted-foreground">
                        {t("render.importRunning", { namespace: refreshNamespace })}
                    </span>
                )}
                {canRefresh && (
                    <button
                        type="button"
                        className={
                            isImportRunning
                                ? "inline-flex h-6 w-6 cursor-wait items-center justify-center rounded text-muted-foreground/50"
                                : "inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                        }
                        onClick={() => {
                            if (isImportRunning) return;
                            parentDoc.onRunImport?.(refreshNamespace);
                        }}
                        disabled={isImportRunning}
                        title={isImportRunning
                            ? t("render.importRunning", { namespace: refreshNamespace })
                            : t("render.rerunImport", { namespace: refreshNamespace })}
                        aria-label={isImportRunning
                            ? t("render.importRunning", { namespace: refreshNamespace })
                            : t("render.rerunImport", { namespace: refreshNamespace })}
                    >
                        {isImportRunning ? (
                            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
                        ) : (
                            <RefreshCw size={13} aria-hidden="true" />
                        )}
                    </button>
                )}
                <span className={lineClass} aria-hidden="true" />
            </div>
            {!collapsed && (
                <>
                    <div className="py-3">
                        <AnnotationProvider
                            documentPath={documentPath}
                            content={sourceContent}
                        >
                            <DocContextProvider value={docValue}>
                                <DocOutputsProvider value={runtime.outputs}>
                                    <ExecutionContextProvider value={execValue}>
                                        <BlockList blocks={snapshot.blocks} />
                                    </ExecutionContextProvider>
                                </DocOutputsProvider>
                            </DocContextProvider>
                        </AnnotationProvider>
                    </div>
                    <div className={delimiterClass}>
                        <span className={lineClass} aria-hidden="true" />
                        <button
                            type="button"
                            className={markerClass}
                            onClick={() => setCollapsed(true)}
                            aria-label={`Collapse import ${label} from end`}
                            title={`Collapse import ${label}: ${snapshot.source}`}
                        >
                            <ChevronUp size={12} className="shrink-0" />
                            <span className="truncate">{label}</span>
                        </button>
                        <span className={lineClass} aria-hidden="true" />
                    </div>
                </>
            )}
        </section>
    );
}
